/**
 * "Questions" — the question tree with a domain overview.
 *
 * Domains → domain overview (areas + ALL open questions + ALL items, everything
 * openable) → area detail (run button, mode picker, snoozed section, open
 * questions, decided questions, items). Items without a subcategory land in
 * "General" instead of a cryptic "(root)". Green run buttons sit on every
 * level: all domains, domain, area (question level lives in the detail view).
 */

import { useState } from "react";
import type { Item } from "../lib/item";
import {
  DEFAULT_MODE_EXPLAINER,
  DEFAULT_MODE_LABEL,
  MODE_EXPLAINERS,
  MODE_LABELS,
  MODES,
  nodeStatusLabel,
  type AreaInfo,
  type DomainInfo,
  type NodeMode,
} from "../lib/nodes";
import { isSnoozed, type AnswerPatch, type Question } from "../lib/questions";
import { StateChip } from "./Chips";
import { QuestionCard } from "./QuestionCard";

type Sel =
  | { kind: "domains" }
  | { kind: "domain"; domain: string }
  | { kind: "area"; path: string };

export function Portfolio({
  tree,
  modes,
  modeNotes,
  extras,
  onToggleExtra,
  stagedPaths,
  onSetMode,
  onOpenItem,
  onOpenQuestion,
  onAnswer,
  onSnooze,
  onRun,
  onScanAll,
}: {
  tree: DomainInfo[];
  /** RAW node modes (only explicit `_node.yml` files — inheritance is resolved here). */
  modes: ReadonlyMap<string, NodeMode | "untouched">;
  /** RAW custom-mode notes per node (only explicit files). */
  modeNotes: ReadonlyMap<string, string>;
  /** Extra agents riding along triggered runs (default: none). */
  extras: ReadonlySet<string>;
  onToggleExtra: (id: string) => void;
  stagedPaths: ReadonlySet<string>;
  onSetMode: (nodePath: string, mode: NodeMode | "untouched", note?: string) => void;
  onOpenItem: (item: Item) => void;
  onOpenQuestion: (q: Question) => void;
  onAnswer: (q: Question, patch: AnswerPatch) => void;
  onSnooze: (q: Question, until: string) => void;
  onRun: (areas: string, label: string, domain?: string) => void;
  /** Scan all: ignore every area mode for one run. */
  onScanAll: () => void;
}) {
  const [sel, setSel] = useState<Sel>({ kind: "domains" });
  const [pickerFor, setPickerFor] = useState<string | null>(null);

  if (tree.length === 0) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
        <h1 className="mb-3 text-xl font-semibold text-ink-100">Questions</h1>
        <div className="card p-6 text-center text-sm text-ink-400">
          No areas yet — refresh the data.
        </div>
      </div>
    );
  }

  if (sel.kind === "area") {
    const found = findArea(tree, sel.path);
    if (found) {
      const { area, domain } = found;
      const rawMode = modes.get(area.path) ?? "untouched";
      const domainMode = modes.get(`agentic/${domain}`) ?? "untouched";
      return (
        <AreaDetail
          area={area}
          domain={domain}
          rawMode={rawMode}
          domainMode={domainMode}
          modeNote={modes2Note(modeNotes, area.path)}
          stagedPaths={stagedPaths}
          pickerOpen={pickerFor === area.path}
          onTogglePicker={() => setPickerFor(pickerFor === area.path ? null : area.path)}
          onSetMode={(mode, note) => {
            setPickerFor(null);
            onSetMode(area.path, mode, note);
          }}
          onBack={() => {
            setSel({ kind: "domain", domain });
            setPickerFor(null);
          }}
          onOpenItem={onOpenItem}
          onOpenQuestion={onOpenQuestion}
          onAnswer={onAnswer}
          onSnooze={onSnooze}
          onRun={() =>
            onRun(area.path, area.area ? `${domain} · ${area.area}` : domain, domain)
          }
        />
      );
    }
  }

  if (sel.kind === "domain") {
    const domain = tree.find((d) => d.domain === sel.domain);
    if (domain) {
      return (
        <DomainOverview
          domain={domain}
          modes={modes}
          modeNotes={modeNotes}
          stagedPaths={stagedPaths}
          onSetMode={onSetMode}
          onBack={() => setSel({ kind: "domains" })}
          onOpenArea={(path) => setSel({ kind: "area", path })}
          onOpenItem={onOpenItem}
          onOpenQuestion={onOpenQuestion}
          onRun={() => onRun(`agentic/${domain.domain}`, `${domain.domain} (domain)`, domain.domain)}
        />
      );
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink-100">Questions</h1>
        <span className="flex items-center gap-1.5">
          <button
            className="btn-run px-3 py-1.5 text-xs"
            title="Trigger a run for everything — follows every area's mode"
            onClick={() => onRun("", "the full nightly run")}
          >
            Run all
          </button>
          <button
            className="btn-ghost px-3 py-1.5 text-xs"
            title="Scan everything, ignoring all area modes (paused areas included)"
            onClick={onScanAll}
          >
            Scan all
          </button>
        </span>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-ink-600">Run extras:</span>
        {EXTRA_AGENTS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => onToggleExtra(id)}
            title="Added to every run you trigger here — off by default; the domain agents always run"
            className={`chip ${
              extras.has(id) ? "bg-emerald-500/15 text-emerald-400" : "bg-ink-850 text-ink-400"
            }`}
          >
            + {label}
          </button>
        ))}
        <span className="text-[11px] text-ink-600">— plain runs stay lean</span>
      </div>
      <div className="space-y-2">
        {tree.map((d) => (
          <button
            key={d.domain}
            className="card flex w-full items-center justify-between p-4 text-left hover:bg-ink-850"
            onClick={() => setSel({ kind: "domain", domain: d.domain })}
          >
            <span className="text-[15px] font-medium capitalize text-ink-100">
              {d.domain}
            </span>
            <span className="flex items-center gap-1.5">
              {d.open > 0 && (
                <span className="chip bg-accent/15 text-accent-soft">{d.open} open</span>
              )}
              <span className="chip bg-ink-850 text-ink-500">{d.total} questions</span>
              <span className="chip bg-ink-850 text-ink-500">
                {d.areas.reduce((n, a) => n + a.items.length, 0)} items
              </span>
              <span className="text-ink-500">›</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function findArea(
  tree: DomainInfo[],
  path: string,
): { area: AreaInfo; domain: string } | null {
  for (const d of tree) {
    for (const a of d.areas) if (a.path === path) return { area: a, domain: d.domain };
  }
  return null;
}

const areaLabel = (area: string) => (area === "" ? "General" : area);

/** Extra agents the founder can add to triggered runs ("additionally"). */
const EXTRA_AGENTS: [string, string][] = [
  ["reviewer", "Reviewer"],
  ["planner", "Planner"],
];

const modes2Note = (notes: ReadonlyMap<string, string>, node: string) => notes.get(node) ?? "";
function DomainOverview({
  domain,
  modes,
  modeNotes,
  stagedPaths,
  onSetMode,
  onBack,
  onOpenArea,
  onOpenItem,
  onOpenQuestion,
  onRun,
}: {
  domain: DomainInfo;
  modes: ReadonlyMap<string, NodeMode | "untouched">;
  modeNotes: ReadonlyMap<string, string>;
  stagedPaths: ReadonlySet<string>;
  onSetMode: (nodePath: string, mode: NodeMode | "untouched", note?: string) => void;
  onBack: () => void;
  onOpenArea: (path: string) => void;
  onOpenItem: (item: Item) => void;
  onOpenQuestion: (q: Question) => void;
  onRun: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const domainMode = modes.get(`agentic/${domain.domain}`) ?? "untouched";
  const [customDraft, setCustomDraft] = useState(
    modeNotes.get(`agentic/${domain.domain}`) ?? "",
  );
  const open: Question[] = [];
  for (const a of domain.areas) for (const q of a.questions) if (q.status === "open") open.push(q);
  const items: Item[] = [];
  const seen = new Set<string>();
  for (const a of domain.areas) {
    for (const i of a.items) {
      if (!seen.has(i.path)) {
        seen.add(i.path);
        items.push(i);
      }
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <button onClick={onBack} className="mb-3 text-sm text-ink-400 hover:text-ink-200">
        ‹ All domains
      </button>

      <div className="card mb-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold capitalize text-ink-100">
              {domain.domain}
            </div>
            <div className="mt-0.5 text-sm text-ink-400">
              {domain.open} open · {domain.total} questions · {domain.areas.length} areas ·{" "}
              {items.length} items
            </div>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              className="btn-run px-3 py-1.5 text-xs"
              title="Trigger a run focused on this domain"
              onClick={onRun}
            >
              Run domain
            </button>
            <button
              className="btn-ghost px-3 py-1.5 text-xs"
              onClick={() => setPickerOpen((v) => !v)}
            >
              {domainMode === "untouched" ? `Mode: ${DEFAULT_MODE_LABEL}` : `Mode: ${MODE_LABELS[domainMode]}`}
            </button>
          </div>
        </div>

        {pickerOpen && (
          <div className="mt-3 space-y-1.5 border-t border-ink-850 pt-3">
            <div className="text-xs text-ink-500">
              Domain default — every area inherits this unless it sets its own mode.
            </div>
            <button
              onClick={() => onSetMode(`agentic/${domain.domain}`, "untouched")}
              className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-ink-850 ${
                domainMode === "untouched" ? "border-accent/40 bg-accent/5" : "border-ink-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-200">
                  {DEFAULT_MODE_LABEL} (default)
                </span>
              </div>
              <div className="mt-1 text-xs leading-relaxed text-ink-500">
                {DEFAULT_MODE_EXPLAINER}
              </div>
            </button>
            {MODES.map((m) => (
              <button
                key={m}
                onClick={() => onSetMode(`agentic/${domain.domain}`, m)}
                className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-ink-850 ${
                  domainMode === m ? "border-accent/40 bg-accent/5" : "border-ink-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-200">{MODE_LABELS[m]}</span>
                  {m === "hold" || m === "waived" || m === "autopilot" ? (
                    <span className="chip bg-ink-800 text-ink-400">quiet</span>
                  ) : (
                    <span className="chip bg-ink-800 text-ink-400">asking</span>
                  )}
                </div>
                <div className="mt-1 text-xs leading-relaxed text-ink-500">
                  {MODE_EXPLAINERS[m]}
                </div>
              </button>
            ))}
            <div
              className={`rounded-xl border p-3 text-left ${
                domainMode === "custom" ? "border-accent/40 bg-accent/5" : "border-ink-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-200">{MODE_LABELS.custom}</span>
                <span className="chip bg-ink-800 text-ink-400">your text</span>
              </div>
              <div className="mt-1 text-xs leading-relaxed text-ink-500">
                {MODE_EXPLAINERS.custom}
              </div>
              <textarea
                className="mt-2 w-full rounded-lg border border-ink-700 bg-ink-950 p-2 text-xs leading-relaxed text-ink-200 placeholder:text-ink-600"
                rows={3}
                placeholder={`e.g. “Try to consolidate and refine only necessary topics and ask new questions regarding them”`}
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
              />
              <button
                className="btn-primary mt-2 w-full py-2 text-xs"
                disabled={!customDraft.trim()}
                onClick={() => onSetMode(`agentic/${domain.domain}`, "custom", customDraft)}
              >
                Save custom mode
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
        Areas
      </div>
      <div className="card mb-6 divide-y divide-ink-850 overflow-hidden">
        {domain.areas.map((a) => (
          <button
            key={a.path}
            className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-ink-850"
            onClick={() => onOpenArea(a.path)}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm capitalize text-ink-200">
                {areaLabel(a.area)}
              </span>
              <span className="text-xs text-ink-500">
                {nodeStatusLabel(a.stats, a.mode)}
                {` · ${a.questions.filter((q) => q.status === "open").length} open · ${a.items.length} item(s)`}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              {a.mode !== "untouched" && (
                <span className="chip bg-ink-800 text-ink-300">
                  {MODE_LABELS[a.mode]}
                  {modes.get(a.path) === undefined ? " · domain" : ""}
                </span>
              )}
              <span className="text-ink-500">›</span>
            </span>
          </button>
        ))}
      </div>

      {open.length > 0 && (
        <>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            All open questions ({open.length})
          </div>
          <div className="mb-6 space-y-2">
            {open.map((q) => (
              <QuestionCard
                key={q.path}
                q={q}
                mode="read"
                staged={stagedPaths.has(q.path)}
                onOpen={() => onOpenQuestion(q)}
              />
            ))}
          </div>
        </>
      )}

      {items.length > 0 && (
        <>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            All items ({items.length})
          </div>
          <div className="space-y-2">
            {items.map((item) => (
              <button
                key={item.path}
                onClick={() => onOpenItem(item)}
                className="card flex w-full items-start gap-3 p-3 text-left hover:bg-ink-850"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink-200">
                    {item.title}
                  </span>
                  <span className="mt-1 flex items-center gap-2 text-xs text-ink-500">
                    <StateChip state={item.state} />
                    {item.subcategory ? areaLabel(item.subcategory) : "General"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AreaDetail({
  area,
  domain,
  rawMode,
  domainMode,
  modeNote,
  stagedPaths,
  pickerOpen,
  onTogglePicker,
  onSetMode,
  onBack,
  onOpenItem,
  onOpenQuestion,
  onAnswer,
  onSnooze,
  onRun,
}: {
  area: AreaInfo;
  domain: string;
  /** The area's OWN mode file ("untouched" = inherits). */
  rawMode: NodeMode | "untouched";
  /** The domain default mode ("untouched" = none set). */
  domainMode: NodeMode | "untouched";
  /** The area's own custom note (prefill for the Custom row). */
  modeNote: string;
  stagedPaths: ReadonlySet<string>;
  pickerOpen: boolean;
  onTogglePicker: () => void;
  onSetMode: (mode: NodeMode | "untouched", note?: string) => void;
  onBack: () => void;
  onOpenItem: (item: Item) => void;
  onOpenQuestion: (q: Question) => void;
  onAnswer: (q: Question, patch: AnswerPatch) => void;
  onSnooze: (q: Question, until: string) => void;
  onRun: () => void;
}) {
  const [customDraft, setCustomDraft] = useState(modeNote);
  const snoozedQ = area.questions.filter((q) => q.status === "open" && isSnoozed(q));
  const openQ = area.questions.filter((q) => q.status === "open" && !isSnoozed(q));
  const doneQ = area.questions.filter((q) => q.status !== "open");

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <button onClick={onBack} className="mb-3 text-sm text-ink-400 hover:text-ink-200">
        ‹ {domain}
      </button>

      <div className="card mb-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold capitalize text-ink-100">
              {domain} · {areaLabel(area.area)}
            </div>
            <div className="mt-0.5 text-sm text-ink-400">
              {nodeStatusLabel(area.stats, area.mode)}
            </div>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              className="btn-run px-3 py-1.5 text-xs"
              title="Trigger a run focused on this area"
              onClick={onRun}
            >
              Run area
            </button>
            <button className="btn-ghost px-3 py-1.5 text-xs" onClick={onTogglePicker}>
              {rawMode !== "untouched"
                ? `Mode: ${MODE_LABELS[rawMode]}`
                : domainMode !== "untouched"
                  ? `Mode: ${MODE_LABELS[domainMode]} · domain`
                  : `Mode: ${DEFAULT_MODE_LABEL}`}
            </button>
          </div>
        </div>

        {pickerOpen && (
          <div className="mt-3 space-y-1.5 border-t border-ink-850 pt-3">
            <button
              onClick={() => onSetMode("untouched")}
              className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-ink-850 ${
                rawMode === "untouched" ? "border-accent/40 bg-accent/5" : "border-ink-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-200">
                  {DEFAULT_MODE_LABEL} (default)
                </span>
                <span className="chip bg-ink-800 text-ink-400">asking</span>
              </div>
              <div className="mt-1 text-xs leading-relaxed text-ink-500">
                {domainMode !== "untouched"
                  ? `Follows this domain's default: ${MODE_LABELS[domainMode]}. ${MODE_EXPLAINERS[domainMode]}`
                  : DEFAULT_MODE_EXPLAINER}
              </div>
            </button>
            {MODES.map((m) => (
              <button
                key={m}
                onClick={() => onSetMode(m)}
                className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-ink-850 ${
                  rawMode === m ? "border-accent/40 bg-accent/5" : "border-ink-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-200">{MODE_LABELS[m]}</span>
                  {m === "hold" || m === "waived" || m === "autopilot" ? (
                    <span className="chip bg-ink-800 text-ink-400">quiet</span>
                  ) : (
                    <span className="chip bg-ink-800 text-ink-400">asking</span>
                  )}
                </div>
                <div className="mt-1 text-xs leading-relaxed text-ink-500">
                  {MODE_EXPLAINERS[m]}
                </div>
              </button>
            ))}
            <div
              className={`rounded-xl border p-3 text-left ${
                rawMode === "custom" ? "border-accent/40 bg-accent/5" : "border-ink-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-200">{MODE_LABELS.custom}</span>
                <span className="chip bg-ink-800 text-ink-400">your text</span>
              </div>
              <div className="mt-1 text-xs leading-relaxed text-ink-500">
                {MODE_EXPLAINERS.custom}
              </div>
              <textarea
                className="mt-2 w-full rounded-lg border border-ink-700 bg-ink-950 p-2 text-xs leading-relaxed text-ink-200 placeholder:text-ink-600"
                rows={3}
                placeholder={`e.g. “Try to consolidate and refine only necessary topics and ask new questions regarding them”`}
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
              />
              <button
                className="btn-primary mt-2 w-full py-2 text-xs"
                disabled={!customDraft.trim()}
                onClick={() => onSetMode("custom", customDraft)}
              >
                Save custom mode
              </button>
            </div>
          </div>
        )}
      </div>

      {snoozedQ.length > 0 && (
        <>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            Snoozed ({snoozedQ.length})
          </div>
          <div className="mb-6 space-y-2">
            {snoozedQ.map((q) => (
              <div key={q.path} className="card flex items-center justify-between gap-2 p-3">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink-200">{q.title}</span>
                  <span className="text-xs text-warn">until {q.snoozeUntil}</span>
                </span>
                <button
                  className="btn-ghost shrink-0 px-3 py-1.5 text-xs"
                  onClick={() => onSnooze(q, "")}
                >
                  Bring back
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {openQ.length > 0 && (
        <>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            Open ({openQ.length})
          </div>
          <div className="space-y-3">
            {openQ.map((q) => (
              <QuestionCard
                key={q.path}
                q={q}
                staged={stagedPaths.has(q.path)}
                onAnswer={(patch) => onAnswer(q, patch)}
                onSnooze={(until) => onSnooze(q, until)}
              />
            ))}
          </div>
        </>
      )}

      {doneQ.length > 0 && (
        <>
          <div className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-ink-500">
            Decided ({doneQ.length})
          </div>
          <div className="space-y-2">
            {doneQ.map((q) => (
              <QuestionCard
                key={q.path}
                q={q}
                mode="read"
                staged={stagedPaths.has(q.path)}
                onOpen={() => onOpenQuestion(q)}
              />
            ))}
          </div>
        </>
      )}

      {area.items.length > 0 && (
        <>
          <div className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-ink-500">
            Items ({area.items.length})
          </div>
          <div className="space-y-2">
            {area.items.map((item) => (
              <button
                key={item.path}
                onClick={() => onOpenItem(item)}
                className="card flex w-full items-start gap-3 p-3 text-left hover:bg-ink-850"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink-200">
                    {item.title}
                  </span>
                  <span className="mt-1 flex items-center gap-2 text-xs text-ink-500">
                    <StateChip state={item.state} />
                    {item.created ?? ""}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
